import React from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { SizeSpacingPanel, ColorsFontsPanel } from '../common/BlockStyleSettings';
import { BLOCK_FEATURES } from '../common/commonDefaults';

/**
 * FaqEditor - Editor component for faq block
 */
const FaqEditor = ({ block, onChange, theme, hideStylePanels }) => {
  const { content } = block;
  const items = content.items || [];

  const handleChange = (field, value) => {
    onChange({ [field]: value });
  };

  const handleStyleChange = (styleUpdates) => {
    onChange({ style: { ...(content.style || {}), ...styleUpdates } });
  };

  const handleItemChange = (itemId, field, value) => {
    const updatedItems = items.map((item) =>
      item.id === itemId ? { ...item, [field]: value } : item
    );
    handleChange('items', updatedItems);
  };

  const addItem = () => {
    const newItem = {
      id: `faq_${Date.now()}`,
      question: '',
      answer: '',
    };
    handleChange('items', [...items, newItem]);
  };

  const removeItem = (itemId) => {
    handleChange('items', items.filter((item) => item.id !== itemId));
  };

  return (
    <div className="space-y-4">
      {/* Heading */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Section Heading
        </label>
        <input
          type="text"
          value={content.heading || ''}
          onChange={(e) => handleChange('heading', e.target.value)}
          placeholder="Frequently Asked Questions"
          className="w-full px-3 py-2 bg-[#1a1a2e] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none"
        />
      </div>

      {/* FAQ Items */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">
          FAQ Items ({items.length})
        </label>

        <div className="space-y-3">
          {items.map((item, index) => (
            <div
              key={item.id}
              className="bg-[#1a1a2e] border border-gray-700 rounded-lg p-3"
            >
              <div className="flex items-start gap-2 mb-2">
                <div className="flex-shrink-0 mt-2 text-gray-500">
                  <GripVertical size={16} />
                </div>
                <div className="flex-1 space-y-2">
                  {/* Question */}
                  <input
                    type="text"
                    value={item.question || ''}
                    onChange={(e) => handleItemChange(item.id, 'question', e.target.value)}
                    placeholder={`Question ${index + 1}`}
                    className="w-full px-3 py-2 bg-[#252540] border border-gray-600 rounded text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none text-sm"
                  />

                  {/* Answer */}
                  <textarea
                    value={item.answer || ''}
                    onChange={(e) => handleItemChange(item.id, 'answer', e.target.value)}
                    placeholder="Answer..."
                    rows={2}
                    className="w-full px-3 py-2 bg-[#252540] border border-gray-600 rounded text-white placeholder-gray-500 focus:border-[#749555] focus:outline-none text-sm resize-none"
                  />
                </div>
                <button
                  onClick={() => removeItem(item.id)}
                  className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-400 transition-colors"
                  title="Remove item"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Add Item Button */}
        <button
          onClick={addItem}
          className="mt-3 w-full py-2.5 border-2 border-dashed border-gray-600 rounded-lg flex items-center justify-center gap-2 text-gray-400 hover:border-[#749555] hover:text-[#749555] transition-colors text-sm"
        >
          <Plus size={18} />
          Add FAQ Item
        </button>
      </div>

      {/* Style Panels */}
      {!hideStylePanels && (<>
      <SizeSpacingPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['faq']}
      />
      <ColorsFontsPanel
        style={content.style || {}}
        onChange={handleStyleChange}
        features={BLOCK_FEATURES['faq']}
        theme={theme || {}}
        blockType="faq"
      />
      </>)}
    </div>
  );
};

export default FaqEditor;
